import * as Yup from "yup";
import { WorkOrderItemType } from "features/WorkOrders/constants/work-order-item.constants";

const BOQ_TYPES = [
  WorkOrderItemType.MATERIAL,
  WorkOrderItemType.LABOR,
  WorkOrderItemType.SERVICE,
] as const;

function hasProductId(productId: unknown): boolean {
  if (productId === "" || productId == null) return false;
  const n = Number(productId);
  return Number.isFinite(n) && n > 0;
}

const itemSchema = Yup.object({
  productId: Yup.mixed().nullable().optional(),
  /** Persisted petty work line id (for material requests); not sent on BOQ save. */
  lineId: Yup.number().nullable().optional(),
  description: Yup.string().nullable().optional(),
  quantity: Yup.number()
    .required("Quantity is required")
    .moreThan(0, "Quantity must be greater than 0"),
  unit: Yup.string().nullable().optional(),
  rate: Yup.mixed().nullable().optional(),
  type: Yup.string()
    .oneOf([...BOQ_TYPES], "Invalid line type")
    .required("Type is required"),
}).test("boq-line-rules", function (val) {
  if (!val) return true;
  const withProduct = hasProductId(val.productId);
  if (!withProduct) {
    const desc = String(val.description ?? "").trim();
    if (!desc) {
      return this.createError({
        path: `${this.path}.description`,
        message: "Description is required",
      });
    }
    const unit = String(val.unit ?? "").trim();
    if (!unit) {
      return this.createError({
        path: `${this.path}.unit`,
        message: "Unit is required",
      });
    }
    const rateRaw = val.rate;
    if (
      rateRaw == null ||
      rateRaw === "" ||
      (typeof rateRaw === "string" && !String(rateRaw).trim())
    ) {
      return this.createError({
        path: `${this.path}.rate`,
        message: "Rate is required",
      });
    }
    if (Number(rateRaw) < 0) {
      return this.createError({
        path: `${this.path}.rate`,
        message: "Rate must be ≥ 0",
      });
    }
  }
  return true;
});

export function buildSchema() {
  return Yup.object({
    title: Yup.string().required("The title field is required"),
    description: Yup.string().nullable().optional(),
    priorityId: Yup.number().required("The priority field is required"),
    sourceChannelId: Yup.number().nullable().optional(),
    clientId: Yup.mixed<number | string>()
      .required("Client is required")
      .test(
        "client-id-positive",
        "Please select a client",
        (v) =>
          v !== "" && v != null && Number.isFinite(Number(v)) && Number(v) > 0,
      ),
    code: Yup.string()
      .trim()
      .transform((v) => (v ? v.toUpperCase() : v))
      .min(4, "Code must be at least 4 characters")
      .max(50, "Code must be at most 50 characters")
      .matches(
        /^[A-Za-z0-9][A-Za-z0-9\-_/&]*[A-Za-z0-9]$/,
        "Code must start and end with a letter or number, and may only contain letters, numbers, hyphens, underscores, slashes, or &",
      )
      .nullable()
      .optional(),

    plannedStartDate: Yup.string()
      .nullable()
      .optional()
      .test("valid-date", "Enter a valid date", (v) => {
        if (!v) return true;
        return !Number.isNaN(new Date(v).getTime());
      }),

    plannedEndDate: Yup.string()
      .nullable()
      .optional()
      .test("valid-date", "Enter a valid date", (v) => {
        if (!v) return true;
        return !Number.isNaN(new Date(v).getTime());
      })
      .test("end-gte-start", "End date cannot be earlier than start date.", function (v) {
        const start = this.parent.plannedStartDate;
        if (!v || !start) return true;
        return v >= start;
      }),

    items: Yup.array()
      .of(itemSchema)
      .min(1, "Please add at least one line item")
      .required("Please add at least one line item"),

    agreedAmount: Yup.number()
      .typeError("Must be a number")
      .min(0, "Cannot be negative")
      .nullable()
      .optional(),
  });
}

export default buildSchema();
